import { useMemo } from "react";
import ProjectStageHeader from "./ProjectStageHeader";
import StageAssignTab from "./StageAssignTab";
import StageSubmissionsTab from "./StageSubmissionsTab";
import StageReviewTab from "./StageReviewTab";
import StageInstructionsPanel from "./StageInstructionsPanel";
import useAuth from "../../hooks/useAuth";
import { PERMISSIONS } from "../../constants/permissions";
import { getCurrentStageIds } from "../../utils/currentStage";

export default function ProjectRightPanel({
  project,
  selectedStageId,
  activeTab,
  onTabChange,
  onAssignStage,
  onReassign,
  onReschedule,
  onRemove,
  onReview,
  loading,
  isAdmin,
}) {
  const stages = project?.workflow?.definition?.stages || [];
  const connections = project?.workflow?.definition?.connections || [];
  const stageDef = stages.find((stage) => stage.id === selectedStageId);

  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const stageAssignments = useMemo(() => {
    return (project?.stageAssignments || [])
      .filter((assignment) => assignment.stageId === selectedStageId)
      .sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));
  }, [project?.stageAssignments, selectedStageId]);

  const activeAssignments = useMemo(() => {
    return stageAssignments.filter((assignment) =>
      ["PENDING", "IN_PROGRESS", "SUBMITTED"].includes(assignment.status),
    );
  }, [stageAssignments]);

  const allSubmitted = useMemo(() => {
    return (
      activeAssignments.length > 0 &&
      activeAssignments.every((assignment) => assignment.status === "SUBMITTED")
    );
  }, [activeAssignments]);

  const outgoingConnections = useMemo(() => {
    return connections
      .filter((connection) => connection.sourceStageId === selectedStageId)
      .map((connection) => ({
        ...connection,
        targetStageName:
          stages.find((stage) => stage.id === connection.targetStageId)?.name ||
          connection.targetStageId,
      }));
  }, [connections, selectedStageId, stages]);

  const isTerminalStage = outgoingConnections.length === 0;
  const sendForReviewEnabled = stageDef?.sendForReview !== false;
  const maxConcurrent = Math.max(
    1,
    Math.floor(Number(stageDef?.maxConcurrentAssignees || 1)),
  );
  const canAssignMore = activeAssignments.length < maxConcurrent;

  const canAssign = isAdmin || permissions.includes(PERMISSIONS.PROJECT_ASSIGN_USERS);

  const stageDone =
    stageAssignments.some((a) => ["DONE", "SKIPPED"].includes(a.status)) &&
    !getCurrentStageIds(project).includes(selectedStageId);

  const tabs = useMemo(() => {
    const list = [];
    if (activeAssignments.length > 0 || stageDone) {
      list.push({ id: "submissions", label: "Submissions" });
    }
    if (canAssign && !stageDone) {
      list.push({
        id: "assign",
        label: activeAssignments.length > 0 ? "Reassign" : "Assign",
      });
    }
    // Instructions live in their own tab rather than inside Assign: they are
    // written and re-read long after the assignment is made, so they must stay
    // reachable once the Assign tab has nothing left to offer.
    if (canAssign && (activeAssignments.length > 0 || stageDone)) {
      list.push({ id: "instructions", label: "Work Instructions" });
    }
    if (allSubmitted && sendForReviewEnabled) {
      list.push({ id: "review", label: "Review" });
    }
    return list;
  }, [activeAssignments, canAssign, allSubmitted, sendForReviewEnabled, stageDone]);

  const currentTab = tabs.find((tab) => tab.id === activeTab)
    ? activeTab
    : tabs[0]?.id ?? null;

  if (!selectedStageId || !stageDef) {
    return (
      <div className="project-right-panel project-right-empty">
        <p>Select a stage from the left panel</p>
      </div>
    );
  }

  return (
    <div className="project-right-panel">
      <ProjectStageHeader
        stageDef={stageDef}
        activeAssignments={activeAssignments}
        isTerminalStage={isTerminalStage}
        isCurrent={getCurrentStageIds(project).includes(selectedStageId)}
      />

      {tabs.length > 0 && (
        <div className="right-panel-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`right-panel-tab ${
                currentTab === tab.id ? "right-panel-tab--active" : ""
              }`}
              onClick={() => onTabChange(tab.id)}
            >
              {tab.label}
              {tab.id === "review" && (
                <span className="tab-dot tab-dot--review" />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="right-panel-content">
        {currentTab === "submissions" && (
          <StageSubmissionsTab
            stageAssignments={stageAssignments}
            activeAssignments={activeAssignments}
            stageDone={stageDone}
          />
        )}
        {currentTab === "assign" &&
          (isAdmin ||
            permissions.includes(PERMISSIONS.PROJECT_ASSIGN_USERS)) && (
            <StageAssignTab
              project={project}
              stageDef={stageDef}
              selectedStageId={selectedStageId}
              activeAssignments={activeAssignments}
              maxConcurrent={maxConcurrent}
              canAssignMore={canAssignMore}
              onAssignStage={onAssignStage}
              onReassign={onReassign}
              onReschedule={onReschedule}
              onRemove={onRemove}
              loading={loading}
            />
          )}
        {currentTab === "instructions" && canAssign && (
          <StageInstructionsPanel
            projectId={project?.id}
            stageId={selectedStageId}
            assignments={
              activeAssignments.length > 0 ? activeAssignments : stageAssignments
            }
            disabled={loading || stageDone}
          />
        )}
        {currentTab === "review" && allSubmitted && (
          <StageReviewTab
            project={project}
            stageDef={stageDef}
            activeAssignments={activeAssignments}
            outgoingConnections={outgoingConnections}
            isTerminalStage={isTerminalStage}
            onReview={onReview}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
}
